import React, { useState, useEffect, useCallback } from 'react';
import {
  Building2, Pencil, Mail, Download, CheckCircle2, X as XIcon,
  Palette, AlertTriangle, Trash2, Database, UploadCloud,
  KeyRound, UserX,
} from 'lucide-react';
import { tenantAdminApi } from '../api/services';
import { useAuth } from '../auth/AuthProvider';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { PageHeader } from '@/components/ui/page-header';
import { LinearProgressBar } from '@/components/ui/linear-progress';
import { cn } from '@/lib/utils';

interface TenantInfo {
  id: string;
  name: string;
  domain?: string;
  status: string;
  plan: string;
  maxExecutiveSeats: number;
  maxEaSeats: number;
  currentExecutives: number;
  currentEAs: number;
  totalUsers: number;
  aiPlanningEnabled: boolean;
  transcriptProcessingEnabled: boolean;
  imageAnalysisEnabled: boolean;
  pdfExportEnabled: boolean;
  webhooksEnabled: boolean;
  apiAccessEnabled: boolean;
  monthlyAiTokenLimit: number;
  monthlyTranscriptLimit: number;
  trialExpiresAt?: string;
  subscriptionExpiresAt?: string;
}

interface TenantUser {
  id: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

const VALID_ROLES = ['Executive', 'ExecutiveAssistant', 'Admin', 'TenantAdmin'];

const ROLE_DESCRIPTIONS: Record<string, string> = {
  Executive: 'Plans and executes their day. Reviews AI-generated plans and approves schedules.',
  ExecutiveAssistant: 'Manages an executive\'s schedule. Creates work items, uploads images, and proposes daily plans.',
  Admin: 'Full tenant administration. Manages users, billing, branding, and organisation settings.',
  TenantAdmin: 'Full tenant administration. Manages users, billing, branding, and organisation settings.',
};

interface BrandingData {
  logoUrl: string;
  primaryColour: string;
  productName: string;
  tagline: string;
  supportEmail: string;
}

const STATUS_TONE: Record<string, string> = {
  Active: 'border-emerald-500 text-emerald-600 dark:text-emerald-400',
  Trialing: 'border-sky-500 text-sky-600 dark:text-sky-400',
  Trial: 'border-sky-500 text-sky-600 dark:text-sky-400',
  Suspended: 'border-amber-500 text-amber-600 dark:text-amber-400',
  Cancelled: 'border-rose-500 text-rose-600 dark:text-rose-400',
};

const ROLE_TONE: Record<string, string> = {
  Admin: 'border-rose-500 text-rose-600 dark:text-rose-400',
  TenantAdmin: 'border-rose-500 text-rose-600 dark:text-rose-400',
  Executive: 'border-primary text-primary',
  ExecutiveAssistant: 'border-violet-500 text-violet-600 dark:text-violet-400',
};

function SeatUsageBar({ label, current, max }: { label: string; current: number; max: number }) {
  const percent = max > 0 ? Math.min((current / max) * 100, 100) : 0;
  const isHigh = percent > 80;
  const isFull = percent >= 100;
  const barTone = isFull ? 'bg-rose-500' : isHigh ? 'bg-amber-500' : 'bg-primary';
  const textTone = isFull ? 'text-rose-500' : isHigh ? 'text-amber-500' : 'text-muted-foreground';

  return (
    <div>
      <div className="mb-1 flex justify-between">
        <span className="text-sm font-medium">{label}</span>
        <span className={cn('text-sm', textTone)}>{current} of {max} seats</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full rounded-full transition-[width]', barTone)}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

function FeatureFlag({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      {enabled
        ? <CheckCircle2 className="h-4 w-4 text-emerald-500" />
        : <XIcon className="h-4 w-4 text-muted-foreground/50" />}
      <span className={cn('text-sm', !enabled && 'text-muted-foreground')}>{label}</span>
    </div>
  );
}

export default function TenantOrganisationPage() {
  const { user } = useAuth();
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [usageTotals, setUsageTotals] = useState<{ aiTokensUsed: number; transcriptsProcessed: number } | null>(null);
  const [usageLimits, setUsageLimits] = useState<{ monthlyAiTokenLimit?: number; monthlyTranscriptLimit?: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [exportLoading, setExportLoading] = useState(false);

  const [editUser, setEditUser] = useState<TenantUser | null>(null);
  const [editRole, setEditRole] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [editSaving, setEditSaving] = useState(false);

  // Reset MFA modal state
  const [resetMfaUser, setResetMfaUser] = useState<TenantUser | null>(null);
  const [resetMfaCode, setResetMfaCode] = useState('');
  const [resetMfaSubmitting, setResetMfaSubmitting] = useState(false);
  const [resetMfaError, setResetMfaError] = useState<string | null>(null);

  // Remove user modal state
  const [removeUser, setRemoveUser] = useState<TenantUser | null>(null);
  const [removeMfaCode, setRemoveMfaCode] = useState('');
  const [removeEmailConfirm, setRemoveEmailConfirm] = useState('');
  const [removeSubmitting, setRemoveSubmitting] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const [branding, setBranding] = useState<BrandingData>({
    logoUrl: '', primaryColour: '#6C9BFF', productName: '', tagline: '', supportEmail: '',
  });
  const [brandingSaving, setBrandingSaving] = useState(false);

  const [useFinancialYear, setUseFinancialYear] = useState(false);
  const [fyStartMonth, setFyStartMonth] = useState(4);
  const [fyLabel, setFyLabel] = useState('');
  const [currentQuarter, setCurrentQuarter] = useState(0);
  const [currentWeek, setCurrentWeek] = useState(0);
  const [fySaving, setFySaving] = useState(false);

  const [retentionDays, setRetentionDays] = useState<number | null>(null);
  const [retentionPreset, setRetentionPreset] = useState<string>('none');
  const [lastRetentionRun, setLastRetentionRun] = useState<string | null>(null);
  const [retentionSaving, setRetentionSaving] = useState(false);

  const [backupLoading, setBackupLoading] = useState(false);
  // setRestoreFile keeps the API parallel to handleRestorePreview even though the
  // value isn't currently rendered — leaving it in case the future restore flow
  // needs the file again after the preview step.
  const [, setRestoreFile] = useState<File | null>(null);
  const [restorePreview, setRestorePreview] = useState<{
    backupVersion: string;
    backupTimestamp: string;
    sourceTenant: string;
    entityCounts: Record<string, number>;
    totalEntities: number;
  } | null>(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const restoreFileInputRef = React.useRef<HTMLInputElement>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tenantResult, usersResult, brandingResult, usageResult, fyResult, retentionResult] = await Promise.allSettled([
        tenantAdminApi.getTenant(),
        tenantAdminApi.getUsers(),
        tenantAdminApi.getBranding(),
        tenantAdminApi.getUsage(),
        tenantAdminApi.getFinancialYear(),
        tenantAdminApi.getRetention(),
      ]);

      if (tenantResult.status === 'fulfilled') setTenant(tenantResult.value);
      if (usersResult.status === 'fulfilled') setUsers(usersResult.value);
      if (brandingResult.status === 'fulfilled' && brandingResult.value) {
        setBranding(prev => ({ ...prev, ...brandingResult.value }));
      }
      if (usageResult.status === 'fulfilled') {
        const u = usageResult.value;
        setUsageTotals(u?.totals ?? null);
        setUsageLimits(u?.limits ?? null);
      }
      if (fyResult.status === 'fulfilled' && fyResult.value) {
        const fy = fyResult.value;
        setUseFinancialYear(fy.useFinancialYear);
        setFyStartMonth(fy.financialYearStartMonth);
        setFyLabel(fy.currentFYLabel);
        setCurrentQuarter(fy.currentQuarter);
        setCurrentWeek(fy.currentFYWeek);
      }
      if (retentionResult.status === 'fulfilled' && retentionResult.value) {
        const ret = retentionResult.value;
        setRetentionDays(ret.dataRetentionDays);
        setLastRetentionRun(ret.lastRetentionRunAt);
        if (ret.dataRetentionDays === null) setRetentionPreset('none');
        else if (ret.dataRetentionDays === 180) setRetentionPreset('6months');
        else if (ret.dataRetentionDays === 365) setRetentionPreset('1year');
        else if (ret.dataRetentionDays === 730) setRetentionPreset('2years');
        else setRetentionPreset('custom');
      }

      if (tenantResult.status === 'rejected' && usersResult.status === 'rejected') {
        setError('Failed to load organisation data.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleEditOpen = (u: TenantUser) => {
    setEditUser(u);
    setEditRole(u.role);
    setEditActive(u.isActive);
  };

  const handleEditSave = async () => {
    if (!editUser) return;
    setEditSaving(true);
    setError(null);
    try {
      if (editRole !== editUser.role) {
        await tenantAdminApi.updateUserRole(editUser.id, editRole);
      }
      if (editActive !== editUser.isActive) {
        await tenantAdminApi.updateUserStatus(editUser.id, editActive);
      }
      setEditUser(null);
      setSuccess('User updated successfully.');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update user.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleResendWelcome = async (userId: string) => {
    setError(null);
    try {
      const result = await tenantAdminApi.resendWelcomeEmail(userId);
      setSuccess(result.message || 'Welcome email sent.');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to send welcome email.');
    }
  };

  const formatStructuredError = (err: any, fallback: string) => {
    const data = err?.response?.data;
    const msg =
      (typeof data === 'object' && (data?.error || data?.message || data?.title)) ||
      err?.message ||
      fallback;
    const correlationId = data?.correlationId || data?.correlation_id;
    return correlationId ? `${msg} (ref: ${correlationId})` : msg;
  };

  const openResetMfa = (u: TenantUser) => {
    setResetMfaUser(u);
    setResetMfaCode('');
    setResetMfaError(null);
  };

  const closeResetMfa = () => {
    if (resetMfaSubmitting) return;
    setResetMfaUser(null);
    setResetMfaCode('');
    setResetMfaError(null);
  };

  const handleResetMfaSubmit = async () => {
    if (!resetMfaUser) return;
    setResetMfaSubmitting(true);
    setResetMfaError(null);
    try {
      await tenantAdminApi.resetUserMfa(resetMfaUser.id, resetMfaCode);
      const name = resetMfaUser.displayName;
      setResetMfaUser(null);
      setResetMfaCode('');
      setSuccess(`MFA reset for ${name}. They'll be prompted to enrol again on next login.`);
      loadData();
    } catch (err: any) {
      setResetMfaError(formatStructuredError(err, 'Failed to reset MFA.'));
    } finally {
      setResetMfaSubmitting(false);
    }
  };

  const openRemoveUser = (u: TenantUser) => {
    setRemoveUser(u);
    setRemoveMfaCode('');
    setRemoveEmailConfirm('');
    setRemoveError(null);
  };

  const closeRemoveUser = () => {
    if (removeSubmitting) return;
    setRemoveUser(null);
    setRemoveMfaCode('');
    setRemoveEmailConfirm('');
    setRemoveError(null);
  };

  const handleRemoveUserSubmit = async () => {
    if (!removeUser) return;
    setRemoveSubmitting(true);
    setRemoveError(null);
    try {
      const id = removeUser.id;
      const name = removeUser.displayName;
      await tenantAdminApi.deleteUser(id, removeMfaCode);
      // Optimistically drop from the table
      setUsers(prev => prev.filter(u => u.id !== id));
      setRemoveUser(null);
      setRemoveMfaCode('');
      setRemoveEmailConfirm('');
      setSuccess(`${name} has been removed from the tenant.`);
      loadData();
    } catch (err: any) {
      setRemoveError(formatStructuredError(err, 'Failed to remove user.'));
    } finally {
      setRemoveSubmitting(false);
    }
  };

  const triggerDownload = (blob: Blob, fallbackName: string, disposition?: string) => {
    let filename = fallbackName;
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match?.[1]) filename = match[1].replace(/['"]/g, '');
    }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    setExportLoading(true);
    setError(null);
    try {
      const response = await tenantAdminApi.exportData();
      triggerDownload(new Blob([response.data], { type: 'application/json' }), 'cadence-export.json', response.headers?.['content-disposition']);
      setSuccess('Data export downloaded successfully.');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to export data.');
    } finally {
      setExportLoading(false);
    }
  };

  const handleSaveBranding = async () => {
    setBrandingSaving(true);
    setError(null);
    try {
      await tenantAdminApi.updateBranding(branding);
      setSuccess('Branding updated successfully.');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update branding.');
    } finally {
      setBrandingSaving(false);
    }
  };

  const handleSaveFY = async () => {
    setFySaving(true);
    setError(null);
    try {
      await tenantAdminApi.updateFinancialYear({
        useFinancialYear,
        startMonth: useFinancialYear ? fyStartMonth : undefined,
      });
      setSuccess('Financial year settings updated.');
      const fy = await tenantAdminApi.getFinancialYear();
      setFyLabel(fy.currentFYLabel);
      setCurrentQuarter(fy.currentQuarter);
      setCurrentWeek(fy.currentFYWeek);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update financial year settings.');
    } finally {
      setFySaving(false);
    }
  };

  const handleRetentionChange = (preset: string) => {
    setRetentionPreset(preset);
    switch (preset) {
      case 'none': setRetentionDays(null); break;
      case '6months': setRetentionDays(180); break;
      case '1year': setRetentionDays(365); break;
      case '2years': setRetentionDays(730); break;
    }
  };

  const handleSaveRetention = async () => {
    setRetentionSaving(true);
    setError(null);
    try {
      const result = await tenantAdminApi.updateRetention(retentionDays);
      setSuccess(result.message || 'Data retention policy updated.');
      setLastRetentionRun(result.lastRetentionRunAt);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update retention policy.');
    } finally {
      setRetentionSaving(false);
    }
  };

  const handleDownloadBackup = async () => {
    setBackupLoading(true);
    setError(null);
    try {
      const response = await tenantAdminApi.downloadBackup();
      triggerDownload(new Blob([response.data], { type: 'application/json' }), 'cadence-backup.json', response.headers?.['content-disposition']);
      setSuccess('Backup downloaded successfully.');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to download backup.');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestorePreview = async (file: File) => {
    setRestoreFile(file);
    setRestoreLoading(true);
    setRestorePreview(null);
    setError(null);
    try {
      const result = await tenantAdminApi.restorePreview(file);
      setRestorePreview(result);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to parse backup file.');
      setRestoreFile(null);
    } finally {
      setRestoreLoading(false);
    }
  };

  const aiUsedPct = (usageTotals && usageLimits?.monthlyAiTokenLimit)
    ? (usageTotals.aiTokensUsed / usageLimits.monthlyAiTokenLimit) : null;
  const transcriptUsedPct = (usageTotals && usageLimits?.monthlyTranscriptLimit)
    ? (usageTotals.transcriptsProcessed / usageLimits.monthlyTranscriptLimit) : null;

  return (
    <div>
      <PageHeader
        icon={Building2}
        title="My Organisation"
        subtitle="Manage your organisation, users, and view plan details"
      />

      {loading && <LinearProgressBar className="mb-2" />}
      {error && (
        <Alert variant="error" className="mb-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert variant="success" className="mb-3">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {/* Org Info */}
        <Card className="h-full">
          <CardContent className="pt-6">
            <h3 className="mb-3 text-base font-semibold">Organisation Info</h3>
            {tenant ? (
              <div className="flex flex-col gap-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Name</p>
                  <p className="font-medium">{tenant.name}</p>
                </div>
                <div className="flex gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Plan</p>
                    <p className="font-semibold text-primary">{tenant.plan}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</p>
                    <Badge
                      variant="outline"
                      className={cn('mt-0.5', STATUS_TONE[tenant.status] || 'border-border text-muted-foreground')}
                    >
                      {tenant.status}
                    </Badge>
                  </div>
                </div>
                {tenant.subscriptionExpiresAt && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Subscription Expires</p>
                    <p className="text-sm">
                      {new Date(tenant.subscriptionExpiresAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                )}
                {tenant.domain && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Domain</p>
                    <p className="text-sm">{tenant.domain}</p>
                  </div>
                )}
              </div>
            ) : !loading ? (
              <p className="py-4 text-center text-sm text-muted-foreground">Unable to load organisation details.</p>
            ) : null}
          </CardContent>
        </Card>

        {/* Seat Usage */}
        <Card className="h-full">
          <CardContent className="pt-6">
            <h3 className="mb-3 text-base font-semibold">Seat Usage</h3>
            {tenant ? (
              <div className="flex flex-col gap-4">
                <SeatUsageBar
                  label="Executive Seats"
                  current={tenant.currentExecutives}
                  max={tenant.maxExecutiveSeats}
                />
                <SeatUsageBar
                  label="EA Seats"
                  current={tenant.currentEAs}
                  max={tenant.maxEaSeats}
                />
                <div className="border-t border-border" />
                <p className="text-sm text-muted-foreground">Total active users: {tenant.totalUsers}</p>
              </div>
            ) : !loading ? (
              <p className="py-4 text-center text-sm text-muted-foreground">Unable to load seat usage.</p>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {/* Plan Features */}
      <Card className="mt-3">
        <CardContent className="pt-6">
          <h3 className="mb-3 text-base font-semibold">Plan Features</h3>
          {tenant ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
              <div className="flex flex-col gap-1.5">
                <FeatureFlag label="AI Planning" enabled={tenant.aiPlanningEnabled} />
                <FeatureFlag label="Transcript Processing" enabled={tenant.transcriptProcessingEnabled} />
                <FeatureFlag label="Image Analysis" enabled={tenant.imageAnalysisEnabled} />
              </div>
              <div className="flex flex-col gap-1.5">
                <FeatureFlag label="PDF Export" enabled={tenant.pdfExportEnabled} />
                <FeatureFlag label="Webhooks" enabled={tenant.webhooksEnabled} />
                <FeatureFlag label="API Access" enabled={tenant.apiAccessEnabled} />
              </div>
              <div className="flex flex-col gap-1.5">
                <p className="text-sm text-muted-foreground">
                  Monthly AI Token Limit: <strong className="text-foreground">{tenant.monthlyAiTokenLimit.toLocaleString()}</strong>
                  {aiUsedPct !== null && (
                    <span className={cn(
                      ' ',
                      aiUsedPct > 0.95 ? 'text-rose-500'
                        : aiUsedPct > 0.8 ? 'text-amber-500'
                        : 'text-muted-foreground',
                    )}>
                      ({Math.round(aiUsedPct * 100)}% used)
                    </span>
                  )}
                </p>
                <p className="text-sm text-muted-foreground">
                  Monthly Transcript Limit: <strong className="text-foreground">{tenant.monthlyTranscriptLimit}</strong>
                  {transcriptUsedPct !== null && (
                    <span className={cn(
                      ' ',
                      transcriptUsedPct > 0.95 ? 'text-rose-500'
                        : transcriptUsedPct > 0.8 ? 'text-amber-500'
                        : 'text-muted-foreground',
                    )}>
                      ({Math.round(transcriptUsedPct * 100)}% used)
                    </span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  ({tenant.maxExecutiveSeats} executive{tenant.maxExecutiveSeats !== 1 ? 's' : ''} × 1.5/day × 30 days)
                </p>
                {aiUsedPct !== null && aiUsedPct > 0.8 && (
                  <Alert variant={aiUsedPct >= 0.95 ? 'error' : 'warning'} className="py-1">
                    <AlertDescription className="text-xs">
                      AI tokens at {Math.round(aiUsedPct * 100)}% of quota
                    </AlertDescription>
                  </Alert>
                )}
                {transcriptUsedPct !== null && transcriptUsedPct > 0.8 && (
                  <Alert variant={transcriptUsedPct >= 0.95 ? 'error' : 'warning'} className="py-1">
                    <AlertDescription className="text-xs">
                      Transcripts at {Math.round(transcriptUsedPct * 100)}% of quota
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </div>
          ) : !loading ? (
            <p className="py-2 text-center text-sm text-muted-foreground">Unable to load feature information.</p>
          ) : null}
        </CardContent>
      </Card>

      {/* Users */}
      <Card className="mt-3">
        <CardContent className="pt-6">
          <h3 className="mb-3 text-base font-semibold">Users</h3>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Email</th>
                  <th className="py-2 pr-3">Role</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Last Login</th>
                  <th className="py-2 pr-3">Created</th>
                  <th className="py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-b border-border/60 hover:bg-muted/50">
                    <td className="py-2 pr-3 font-medium">
                      {u.displayName}
                      {u.id === user?.id && (
                        <Badge variant="secondary" className="ml-2 h-5">You</Badge>
                      )}
                    </td>
                    <td className="py-2 pr-3">{u.email}</td>
                    <td className="py-2 pr-3">
                      <Badge variant="outline" className={ROLE_TONE[u.role] || 'border-border text-muted-foreground'}>
                        {u.role}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3">
                      <Badge
                        variant="outline"
                        className={u.isActive
                          ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                          : 'border-rose-500 text-rose-600 dark:text-rose-400'}
                      >
                        {u.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground">
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString('en-GB') : 'Never'}
                    </td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground">
                      {new Date(u.createdAt).toLocaleDateString('en-GB')}
                    </td>
                    <td className="py-2 text-right">
                      <TooltipProvider>
                        {u.id !== user?.id && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEditOpen(u)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Edit user</TooltipContent>
                          </Tooltip>
                        )}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleResendWelcome(u.id)}>
                              <Mail className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Resend welcome email</TooltipContent>
                        </Tooltip>
                        {u.id !== user?.id && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openResetMfa(u)}>
                                <KeyRound className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Reset MFA</TooltipContent>
                          </Tooltip>
                        )}
                        {u.id !== user?.id && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-rose-600 hover:bg-rose-500/10 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300"
                                onClick={() => openRemoveUser(u)}
                              >
                                <UserX className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Remove user</TooltipContent>
                          </Tooltip>
                        )}
                      </TooltipProvider>
                    </td>
                  </tr>
                ))}
                {!loading && users.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-4 text-center text-sm text-muted-foreground">No users found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Financial Year */}
      <Card className="mt-3">
        <CardContent className="pt-6">
          <h3 className="mb-3 text-base font-semibold">Financial Year</h3>
          <label className="flex cursor-pointer items-center gap-2">
            <Switch checked={useFinancialYear} onCheckedChange={setUseFinancialYear} />
            <span className="text-sm">Use Financial Year (instead of Calendar Year)</span>
          </label>

          {useFinancialYear && (
            <div className="mt-3 max-w-xs">
              <Select value={String(fyStartMonth)} onValueChange={(v) => setFyStartMonth(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[
                    [1, 'January'], [2, 'February'], [3, 'March'],
                    [4, 'April (UK Standard)'], [5, 'May'], [6, 'June'],
                    [7, 'July (Australia)'], [8, 'August'], [9, 'September'],
                    [10, 'October (US Federal)'], [11, 'November'], [12, 'December'],
                  ].map(([n, label]) => (
                    <SelectItem key={n} value={String(n)}>{label as string}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <p className="mt-3 text-sm text-muted-foreground">
            Current: {fyLabel || 'Loading...'} · Q{currentQuarter} · Week {currentWeek}
          </p>

          <Button onClick={handleSaveFY} disabled={fySaving} className="mt-3">
            {fySaving ? 'Saving...' : 'Save'}
          </Button>
        </CardContent>
      </Card>

      {/* Branding */}
      <Card className="mt-3">
        <CardContent className="pt-6">
          <div className="mb-2 flex items-center gap-1.5">
            <Palette className="h-5 w-5 text-primary" />
            <h3 className="text-base font-semibold">Branding</h3>
          </div>
          <p className="mb-3 text-sm text-muted-foreground">Customise the look and feel of Cadence for your organisation.</p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="b-logo">Logo URL</Label>
              <Input
                id="b-logo"
                value={branding.logoUrl}
                onChange={e => setBranding(prev => ({ ...prev, logoUrl: e.target.value }))}
                placeholder="https://example.com/logo.png"
              />
              <p className="text-xs text-muted-foreground">URL to your organisation's logo</p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="b-color">Primary Colour</Label>
              <Input
                id="b-color"
                type="color"
                value={branding.primaryColour}
                onChange={e => setBranding(prev => ({ ...prev, primaryColour: e.target.value }))}
                className="h-10"
              />
              <p className="text-xs text-muted-foreground">Brand accent colour used throughout the interface</p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="b-name">Custom Product Name</Label>
              <Input
                id="b-name"
                value={branding.productName}
                onChange={e => setBranding(prev => ({ ...prev, productName: e.target.value }))}
                placeholder="e.g. My Company Command Centre"
              />
              <p className="text-xs text-muted-foreground">Replaces 'Cadence' in the UI for your users</p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="b-tagline">Tagline</Label>
              <Input
                id="b-tagline"
                value={branding.tagline}
                onChange={e => setBranding(prev => ({ ...prev, tagline: e.target.value }))}
                placeholder="e.g. Executive excellence, every day"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="b-support">Support Email</Label>
              <Input
                id="b-support"
                type="email"
                value={branding.supportEmail}
                onChange={e => setBranding(prev => ({ ...prev, supportEmail: e.target.value }))}
                placeholder="support@yourcompany.com"
              />
              <p className="text-xs text-muted-foreground">Shown as the support contact for your users</p>
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button onClick={handleSaveBranding} disabled={brandingSaving}>
              {brandingSaving ? 'Saving...' : 'Save Branding'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Data Retention */}
      <Card className="mt-3">
        <CardContent className="pt-6">
          <div className="mb-2 flex items-center gap-1.5">
            <Trash2 className="h-5 w-5 text-primary" />
            <h3 className="text-base font-semibold">Data Retention</h3>
          </div>
          <p className="mb-3 text-sm text-muted-foreground">
            Configure how long completed and cancelled data is retained. After the retention period,
            completed work items, past calendar events, and reflections are permanently deleted.
          </p>
          <Alert variant="warning" className="mb-3">
            <AlertDescription>
              Changing the retention policy affects all historical data. Deleted data cannot be recovered.
            </AlertDescription>
          </Alert>
          <div className="flex flex-col gap-3">
            <div className="flex max-w-xs flex-col gap-1.5">
              <Label>Retention Period</Label>
              <Select value={retentionPreset} onValueChange={handleRetentionChange}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Keep Forever (no auto-deletion)</SelectItem>
                  <SelectItem value="6months">6 Months (180 days)</SelectItem>
                  <SelectItem value="1year">1 Year (365 days)</SelectItem>
                  <SelectItem value="2years">2 Years (730 days)</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {retentionPreset === 'custom' && (
              <div className="flex max-w-[200px] flex-col gap-1.5">
                <Label htmlFor="rd-days">Custom Retention (days)</Label>
                <Input
                  id="rd-days"
                  type="number"
                  min={30}
                  value={retentionDays ?? ''}
                  onChange={e => setRetentionDays(e.target.value ? parseInt(e.target.value, 10) : null)}
                />
                <p className="text-xs text-muted-foreground">Minimum 30 days</p>
              </div>
            )}
            {lastRetentionRun && (
              <p className="text-sm text-muted-foreground">
                Last cleanup run: {new Date(lastRetentionRun).toLocaleString()}
              </p>
            )}
            <div>
              <Button size="sm" onClick={handleSaveRetention} disabled={retentionSaving}>
                {retentionSaving ? 'Saving...' : 'Save Retention Policy'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Export */}
      <Card className="mt-3">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold">Data Export</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Download all your organisation's data as JSON (GDPR compliance).
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={exportLoading}
              className="gap-1.5"
            >
              <Download className="h-4 w-4" />
              {exportLoading ? 'Exporting...' : 'Export Data'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Backup & Restore */}
      <Card className="mt-3">
        <CardContent className="pt-6">
          <div className="mb-3 flex items-center gap-1.5">
            <Database className="h-5 w-5 text-primary" />
            <h3 className="text-base font-semibold">Backup & Restore</h3>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-2">
              <p className="text-sm font-semibold">Download Backup</p>
              <p className="text-sm text-muted-foreground">
                Export a comprehensive backup of all your organisation's data including work items,
                projects, drivers, KPIs, reflections, preferences, and more.
              </p>
              <div>
                <Button
                  variant="outline"
                  onClick={handleDownloadBackup}
                  disabled={backupLoading}
                  className="gap-1.5"
                >
                  <Database className="h-4 w-4" />
                  {backupLoading ? 'Generating backup...' : 'Download Backup'}
                </Button>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-semibold">Restore from Backup</p>
                <Badge variant="outline" className="border-sky-500 text-[0.65rem] text-sky-600 dark:text-sky-400">
                  Coming Soon
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Upload a backup file to preview what would be restored. Full restore will be available in a future update.
              </p>
              <div>
                <input
                  type="file"
                  ref={restoreFileInputRef}
                  accept=".json"
                  style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) handleRestorePreview(file);
                    e.target.value = '';
                  }}
                />
                <Button
                  variant="outline"
                  onClick={() => restoreFileInputRef.current?.click()}
                  disabled={restoreLoading}
                  className="gap-1.5"
                >
                  <UploadCloud className="h-4 w-4" />
                  {restoreLoading ? 'Analysing...' : 'Upload Backup File'}
                </Button>
              </div>
              {restoreLoading && <LinearProgressBar />}
              {restorePreview && (
                <Card className="p-3">
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-semibold">Backup Preview</p>
                    <p className="text-xs text-muted-foreground">
                      Source: {restorePreview.sourceTenant} | Version: {restorePreview.backupVersion} | Created: {new Date(restorePreview.backupTimestamp).toLocaleString()}
                    </p>
                    <div className="my-1 border-t border-border" />
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(restorePreview.entityCounts).map(([entity, count]) => (
                        <Badge
                          key={entity}
                          variant="outline"
                          className="h-[22px] text-[0.65rem]"
                        >
                          {entity}: {count}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground">Total entities: {restorePreview.totalEntities}</p>
                    <Alert className="py-1">
                      <AlertDescription>
                        Full restore functionality is coming soon. This is a preview only.
                      </AlertDescription>
                    </Alert>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cancel Account */}
      <Card className="mt-3 border-rose-500/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="mb-0.5 flex items-center gap-1.5">
                <AlertTriangle className="h-5 w-5 text-rose-500" />
                <h3 className="text-base font-semibold text-rose-500">Cancel Account</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Permanently cancel your organisation's subscription and deactivate all users. This action cannot be undone.
              </p>
            </div>
            <Button
              variant="outline"
              asChild
              className="whitespace-nowrap border-rose-500/40 text-rose-500 hover:bg-rose-500/10"
            >
              <a href="/billing">Go to Billing</a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={(o) => { if (!o) setEditUser(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          {editUser && (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-muted-foreground">
                {editUser.displayName} ({editUser.email})
              </p>
              <div className="flex flex-col gap-1.5">
                <Label>Role</Label>
                <Select value={editRole} onValueChange={setEditRole}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VALID_ROLES.map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{ROLE_DESCRIPTIONS[editRole] || ''}</p>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Status</Label>
                <Select
                  value={editActive ? 'active' : 'inactive'}
                  onValueChange={(v) => setEditActive(v === 'active')}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditUser(null)}>Cancel</Button>
            <Button onClick={handleEditSave} disabled={editSaving}>
              {editSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset MFA Dialog */}
      <Dialog open={!!resetMfaUser} onOpenChange={(o) => { if (!o) closeResetMfa(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {resetMfaUser ? `Reset MFA for ${resetMfaUser.displayName}?` : 'Reset MFA'}
            </DialogTitle>
          </DialogHeader>
          {resetMfaUser && (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-muted-foreground">
                {resetMfaUser.displayName} ({resetMfaUser.email}) will be signed out of all sessions
                and prompted to enrol their authenticator app again on next login. This action is
                logged in your tenant audit log.
              </p>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="reset-mfa-code">Your MFA Code</Label>
                <Input
                  id="reset-mfa-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="123456"
                  value={resetMfaCode}
                  onChange={e => setResetMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="font-mono tracking-[0.3em] text-center"
                />
                <p className="text-xs text-muted-foreground">
                  Enter the 6-digit code from your own authenticator app to confirm.
                </p>
              </div>
              {resetMfaError && (
                <Alert variant="error">
                  <AlertDescription>{resetMfaError}</AlertDescription>
                </Alert>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={closeResetMfa} disabled={resetMfaSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleResetMfaSubmit}
              disabled={resetMfaSubmitting || resetMfaCode.length !== 6}
            >
              {resetMfaSubmitting ? 'Resetting...' : 'Reset MFA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove User Dialog */}
      <Dialog open={!!removeUser} onOpenChange={(o) => { if (!o) closeRemoveUser(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {removeUser ? `Remove ${removeUser.displayName} from the tenant?` : 'Remove user'}
            </DialogTitle>
          </DialogHeader>
          {removeUser && (
            <div className="flex flex-col gap-3">
              <Alert variant="warning">
                <AlertDescription>
                  Their active sessions end immediately, work items they were assigned to are
                  reassigned to unassigned, and their account is deactivated. The user record is
                  retained for audit but no longer counts against your seat licence. This action is
                  logged.
                </AlertDescription>
              </Alert>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="remove-email-confirm">
                  Type <span className="font-mono text-foreground">{removeUser.email}</span> to confirm
                </Label>
                <Input
                  id="remove-email-confirm"
                  autoComplete="off"
                  value={removeEmailConfirm}
                  onChange={e => setRemoveEmailConfirm(e.target.value)}
                  placeholder={removeUser.email}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="remove-mfa-code">Your MFA Code</Label>
                <Input
                  id="remove-mfa-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="123456"
                  value={removeMfaCode}
                  onChange={e => setRemoveMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="font-mono tracking-[0.3em] text-center"
                />
                <p className="text-xs text-muted-foreground">
                  Enter the 6-digit code from your own authenticator app to confirm.
                </p>
              </div>
              {removeError && (
                <Alert variant="error">
                  <AlertDescription>{removeError}</AlertDescription>
                </Alert>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={closeRemoveUser} disabled={removeSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleRemoveUserSubmit}
              disabled={
                removeSubmitting ||
                removeMfaCode.length !== 6 ||
                !removeUser ||
                removeEmailConfirm.trim().toLowerCase() !== removeUser.email.trim().toLowerCase()
              }
              className="bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500"
            >
              {removeSubmitting ? 'Removing...' : 'Remove user'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
